import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from 'react'
import { useDataEngine, useConfig } from '@dhis2/app-runtime'
import { useNavigate } from 'react-router-dom'
import { useSession } from './SessionContext'

// Import shared utilities with your actual IDs
import {
  POSITION_ATTRIBUTES,
  POSITION_PROGRAM_ID,
  FIND_USER_POSITION_QUERY,
  findReportingManager,
  getTeiOrgUnit
} from '../auth/utils/reportingStructure'

const TOP_LEVEL_ORG_ID = 'YbzM5Sy37YS'

const ME_QUERY = {
  me: {
    resource: 'me',
    params: {
      fields:
        'id,username,name,email,phoneNumber,organisationUnits[id,name],userCredentials[userRoles[id,name]]',
    },
  },
}

const AuthContext = createContext()

const userHasRealOrg = (user) =>
  Array.isArray(user.organisationUnits) &&
  user.organisationUnits.some(ou => ou.id !== TOP_LEVEL_ORG_ID)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [initialLoad, setInitialLoad] = useState(true)
  const [registrationComplete, setRegistrationComplete] = useState(false)
  const [managerOrgUnit, setManagerOrgUnit] = useState(null)

  const engine = useDataEngine()
  const { baseUrl } = useConfig()
  const navigate = useNavigate()
  const { sessionExpired, setSessionExpired } = useSession()

  const [currentUserPosition, setCurrentUserPosition] = useState(null)
  const [reportingToPosition, setReportingToPosition] = useState(null)
  const [directReportPositions, setDirectReportPositions] = useState([])
  const [reportingStructureLoaded, setReportingStructureLoaded] = useState(false)

  const intervalRef = useRef(null)

  // Helper: Extract attributes into a map
  const extractAttributes = (attributes = []) => {
    const map = {}
    attributes.forEach((attr) => {
      map[attr.attribute] = attr.value
    })
    return map
  }

  const loadReportingStructure = useCallback(async (userPosition, authHeader) => {
  try {
    if (!userPosition) {
      console.log('❌ Cannot load reporting structure - no user position')
      return null
    }
    
    // Extract attributes
    const attributes = extractAttributes(userPosition.attributes)
    
    // 1. Get reporting manager
    let reportingManagerPosition = null
    const reportingToTei = attributes[POSITION_ATTRIBUTES.REPORTING_TO] || attributes['SjP47qi3JeB']
    
    if (reportingToTei) {
      console.log('🔍 Finding reporting manager:', reportingToTei)
      
      const url = `${baseUrl}/api/trackedEntityInstances/${reportingToTei}?fields=trackedEntityInstance,orgUnit,attributes[attribute,value]`
      
      const response = await fetch(url, {
        headers: { Authorization: `Basic ${authHeader}` },
      })
      
      if (response.ok) {
        const managerData = await response.json()
        reportingManagerPosition = {
          trackedEntityInstance: managerData.trackedEntityInstance,
          orgUnit: managerData.orgUnit,
          attributes: managerData.attributes || [],
          extractedAttributes: extractAttributes(managerData.attributes)
        }
        console.log('✅ Reporting manager found:', reportingManagerPosition.extractedAttributes[POSITION_ATTRIBUTES.POSITION_TITLE])
      }
    }
    
    // 2. Get direct reports (people who report to this position)
    let directReports = []
    
    const directReportsUrl = `${baseUrl}/api/trackedEntityInstances?trackedEntityType=UGGo0Ebjl0E&ouMode=ACCESSIBLE&filter=SjP47qi3JeB:EQ:${userPosition.trackedEntityInstance}&fields=trackedEntityInstance,orgUnit,attributes[attribute,value]&pageSize=50`
    
    const directReportsResponse = await fetch(directReportsUrl, {
      headers: { Authorization: `Basic ${authHeader}` },
    })
    
    if (directReportsResponse.ok) {
      const directReportsData = await directReportsResponse.json()
      directReports = (directReportsData.trackedEntityInstances || []).map(pos => ({
        trackedEntityInstance: pos.trackedEntityInstance,
        orgUnit: pos.orgUnit,
        attributes: pos.attributes || [],
        extractedAttributes: extractAttributes(pos.attributes)
      }))
      console.log('✅ Direct reports found:', directReports.length)
    }
    
    return {
      reportingManager: reportingManagerPosition,
      directReports: directReports
    }
    
  } catch (error) {
    console.error('❌ Error loading reporting structure:', error)
    return null
  }
}, [baseUrl])

  // Function to find user's current position
const findUserPosition = useCallback(async (userId, authHeader) => {
  try {
    console.log('🔍 Safe position lookup for:', userId)
    
    // Try user's own permissions first
    const response = await fetch(
      `${baseUrl}/api/trackedEntityInstances?trackedEntityType=UGGo0Ebjl0E&ouMode=ACCESSIBLE&filter=V66IFsw5kq6:EQ:${userId}&fields=trackedEntityInstance,orgUnit,attributes[attribute,value]&pageSize=1`,
      {
        headers: { 
          Authorization: `Basic ${authHeader}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      }
    )
    
    if (response.ok) {
      const data = await response.json()
      if (data.trackedEntityInstances?.length > 0) {
        console.log('✅ Position found with user permissions')
        return data.trackedEntityInstances[0]
      }
    }
    
    // If not found, the user might not have proper access
    console.log('⚠️ Position not found - user may need data sharing configuration')
    return null
    
  } catch (error) {
    console.error('❌ Error:', error)
    return null
  }
}, [baseUrl])

  // Dynamic manager org unit discovery
  const discoverManagerOrgUnit = useCallback(async (userId, searchOrgUnit, authHeader) => {
      try {
        console.log('🔍 STEP 1: Finding position for user:', userId)

        const userPosition = await findUserPosition(userId, searchOrgUnit, authHeader)
        
        console.log('📍 User position found:', userPosition)
        
        if (!userPosition) {
          console.log('❌ No position found for user')
          return null
        }

        // Check if this is a CEO/top-level position
        const isCEOPosition = userPosition.isExecutivePosition || 
                            userPosition.isTopLevel || 
                            userPosition.isVirtualPosition ||
                            !userPosition.extractedAttributes?.['SjP47qi3JeB'] // No reportingTo attribute
        
        console.log('👑 Is CEO/Top-level position?', isCEOPosition)
        
        if (isCEOPosition) {
          console.log('👑 CEO detected - skipping reporting manager lookup')
          return null  // CEO has no manager org unit
        }

    // Extract the TEI
    const userTei = userPosition.trackedEntityInstance
    
    if (!userTei) {
      console.log('❌ Position found but has no TEI')
      return null
    }

    // Ensure user has data view access to the position's org unit
    if (userPosition.orgUnit) {
      console.log('🔍 Ensuring data view access to position org unit:', userPosition.orgUnit)
      const accessGranted = await grantOrgUnitAccess(userId, userPosition.orgUnit, authHeader)
      if (accessGranted) {
        console.log('✅ Data view access granted to position org unit')
      }
    }

    console.log('🔍 STEP 2: Finding reporting manager for TEI:', userTei)
    
    const reportingManager = await findReportingManager(userTei, authHeader, baseUrl)
    
    if (!reportingManager) {
      console.log('❌ No reporting manager found')
      return null
    }
    
    // Extract manager TEI
    const managerTei = reportingManager.trackedEntityInstance
    
    if (!managerTei) {
      console.log('❌ Reporting manager found but has no TEI')
      return null
    }

    console.log('🔍 STEP 3: Getting manager org unit for TEI:', managerTei)
    const managerOrgUnitId = await getTeiOrgUnit(managerTei, authHeader, baseUrl)
    
    if (!managerOrgUnitId) {
      console.log('❌ Cannot determine manager org unit')
      return null
    }

    console.log('✅ Manager org unit:', managerOrgUnitId)
    return managerOrgUnitId

  } catch (error) {
    console.error('❌ Error in discoverManagerOrgUnit:', error)
    return null
  }
}, [findUserPosition, baseUrl])

// Alternative: Use PATCH method (safer)
const grantOrgUnitAccess = useCallback(async (userId, orgUnitId, authHeader) => {
  try {
    console.log(`🔗 Granting access to org unit ${orgUnitId} for user ${userId}`)
    
    // First check if already has access
    const userResponse = await fetch(`${baseUrl}/api/users/${userId}.json?fields=organisationUnits[id],dataViewOrganisationUnits[id]`, {
      headers: { 'Authorization': `Basic ${authHeader}` }
    })
    
    if (!userResponse.ok) {
      console.error('Failed to get user:', await userResponse.text())
      return false
    }
    
    const userData = await userResponse.json()
    const currentOrgUnits = userData.organisationUnits?.map(ou => ou.id) || []
    const currentDataViewOrgs = userData.dataViewOrganisationUnits?.map(ou => ou.id) || []
    
    const hasOrgUnitAccess = currentOrgUnits.includes(orgUnitId)
    const hasDataViewAccess = currentDataViewOrgs.includes(orgUnitId)
    
    if (hasOrgUnitAccess && hasDataViewAccess) {
      console.log('✅ User already has access to this org unit')
      return true
    }
    
    // Build PATCH operations
    const patchOperations = []
    
    if (!hasOrgUnitAccess) {
      patchOperations.push({
        "op": "add",
        "path": "/organisationUnits/-",
        "value": { "id": orgUnitId }
      })
    }
    
    if (!hasDataViewAccess) {
      patchOperations.push({
        "op": "add",
        "path": "/dataViewOrganisationUnits/-", 
        "value": { "id": orgUnitId }
      })
    }
    
    if (patchOperations.length === 0) {
      console.log('✅ No updates needed')
      return true
    }
    
    console.log('📦 PATCH operations:', JSON.stringify(patchOperations, null, 2))
    
    const updateResponse = await fetch(`${baseUrl}/api/users/${userId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Basic ${authHeader}`,
        'Content-Type': 'application/json-patch+json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(patchOperations),
    })
    
    if (!updateResponse.ok) {
      const errorText = await updateResponse.text()
      console.error('Failed to update user:', errorText)
      return false
    }
    
    console.log('✅ Successfully granted org unit access')
    return true
    
  } catch (error) {
    console.error('❌ Error granting org unit access:', error)
    return false
  }
}, [baseUrl])

  // Main function to grant manager data access
  const grantManagerDataAccess = useCallback(async (userData, authHeader) => {
    try {
      const userId = userData?.id
      const searchOrgUnit = userData?.organisationUnits?.[0]?.id

      // If no userId, findUserPosition will get it from /me
      if (!userId) {
        console.log('⚠️ No user ID provided, findUserPosition will get current user from /me')
      }

      // If no org unit assigned, we'll search across all accessible org units
      if (!searchOrgUnit) {
        console.log('⚠️ No organisation unit found for user, searching across all accessible org units')
      }

      console.log('🔐 Granting manager data access for user:', userId || 'current user')
      console.log('🔐 Search org unit:', searchOrgUnit || 'All accessible')
      console.log('🔐 Auth header present:', !!authHeader)

      // Dynamically discover manager's org unit
      const managerOrgUnitId = await discoverManagerOrgUnit(userId, searchOrgUnit, authHeader)

      if (!managerOrgUnitId) {
        console.log('⚠️ Cannot grant manager access - no manager org unit found')
        return null
      }

      // Check if user already has access (optional optimization)
      const userOrgUnits = userData.organisationUnits?.map(ou => ou.id) || []

      if (userOrgUnits.includes(managerOrgUnitId)) {
        console.log('✅ User already has access to manager org unit')
        setManagerOrgUnit(managerOrgUnitId)
        return managerOrgUnitId
      }

      // Grant access
      const success = await grantOrgUnitAccess(userId, managerOrgUnitId, authHeader)

      if (success) {
        setManagerOrgUnit(managerOrgUnitId)
        console.log('✅ Manager data access granted')
        return managerOrgUnitId
      }

      return null

    } catch (error) {
      console.error('❌ Error granting manager data access:', error)
      return null
    }
  }, [discoverManagerOrgUnit, grantOrgUnitAccess, baseUrl])

  const isProcessingRef = useRef(false);

const processUser = useCallback(async (userData, authHeader = null) => {
  console.log('🔄 Processing user:', userData.username)

  const roles = userData?.userCredentials?.userRoles || []
  const roleIds = roles.map(role => role.id)

  // First set basic user info
  const userWithRoles = {
    ...userData,
    roleIds,
    authorities: roleIds,
  }

  // Set position-related state to null (position loading removed from auth process)
  setCurrentUserPosition(null)
  setReportingToPosition(null)
  setDirectReportPositions([])
  setReportingStructureLoaded(true)

  // Set the user without position info
  setUser({
    ...userWithRoles,
    position: null,
    hasPosition: false,
    isTopLevel: false,
    isCEO: false
  })

  setRegistrationComplete(userHasRealOrg(userData))

  // Note: Manager data access grant removed as it depends on position
}, [])


  // Memoized verifyAuth to avoid re-creation on every render
  const verifyAuth = useCallback(
    async (shouldNavigateOnFail = true) => {
      console.log('🔐 verifyAuth called with shouldNavigateOnFail:', shouldNavigateOnFail)
      setLoading(true)
      try {
        const authHeader = localStorage.getItem('dhis2_auth')
        if (!authHeader) {
          console.log('❌ No auth token found in localStorage')
          throw new Error('No auth token found in localStorage')
        }

        console.log('🔐 Querying /me endpoint')
        const { me } = await engine.query(ME_QUERY, {
          headers: { Authorization: `Basic ${authHeader}` },
        })

        console.log('✅ Auth verification successful, processing user')
        processUser(me, authHeader)
        setError(null)
        setSessionExpired(false) // Clear expired flag on success
        return true
      } catch (err) {
        console.error('❌ Auth verification failed:', err)
        localStorage.removeItem('dhis2_auth')
        setUser(null)
        setRegistrationComplete(false)
        setError('Session expired or invalid. Please log in again.')
        if (shouldNavigateOnFail) {
          console.log('🔄 Navigating to /login due to auth failure')
          navigate('/login', { replace: true })
        } else {
          console.log('🔄 Setting session expired flag')
          setSessionExpired(true)
        }
        return false
      } finally {
        setLoading(false)
        if (initialLoad) setInitialLoad(false)
      }
    },
    [engine, navigate, processUser, initialLoad]
  )

  // Run verifyAuth on component mount
  useEffect(() => {
    console.log('🔄 AuthContext useEffect triggered, current pathname:', window.location.pathname)
    const publicRoutes = ['/login', '/register']
    const isPublicRoute = publicRoutes.includes(window.location.pathname)
    console.log('🔄 Is public route:', isPublicRoute)

    if (!isPublicRoute) {
      console.log('🔄 Calling verifyAuth(true) for non-public route')
      verifyAuth(true)
    } else {
      console.log('🔄 Calling verifyAuth(false) for public route')
      verifyAuth(false).then((isAuthenticated) => {
        console.log('🔄 verifyAuth result for public route:', isAuthenticated)
        if (isAuthenticated && window.location.pathname === '/login') {
          console.log('🔄 Redirecting authenticated user from /login to /dashboard')
          navigate('/dashboard', { replace: true })
        }
      })
    }
    // It's safe to list verifyAuth & navigate since they are memoized
  }, [verifyAuth, navigate])

  // Periodic session keep-alive
  useEffect(() => {
    if (user) {
      intervalRef.current = setInterval(async () => {
        try {
          const authHeader = localStorage.getItem('dhis2_auth')
          if (authHeader) {
            await fetch('/api/system/info', {
              headers: { Authorization: `Basic ${authHeader}` },
              credentials: 'include',
            })
          }
        } catch (err) {
          console.error('Session keep-alive failed:', err)
        }
      }, 50 * 60 * 1000) // 50 minutes
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [user])

  // Login function: verify with backend, process and store auth header
  const login = useCallback(
    async (username, password) => {
      setLoading(true)
      setError(null)
      try {
        const encodedAuth = btoa(`${username}:${password}`)
        const response = await fetch('/api/me', {
          headers: {
            Authorization: `Basic ${encodedAuth}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        })

        if (!response.ok) {
          throw new Error('Invalid credentials')
        }

        const userData = await response.json()
        processUser(userData, encodedAuth)

        localStorage.setItem('dhis2_auth', encodedAuth)
        return true
      } catch (error) {
        setError('Invalid username or password')
        setUser(null)
        setRegistrationComplete(false)
        return false
      } finally {
        setLoading(false)
      }
    },
    [processUser]
  )

  // Logout function: clear storage, backend logout, then navigate to login
  const logout = useCallback(async () => {
    setUser(null)
    setError(null)
    setRegistrationComplete(false)
    setInitialLoad(true)
    localStorage.removeItem('dhis2_auth')

    try {
      await fetch(`${baseUrl}/dhis-web-commons-security/logout.action`, {
        credentials: 'include',
      })
    } catch (err) {
      console.error('Error calling backend logout:', err)
    } finally {
      navigate('/login', { replace: true })
    }
  }, [baseUrl, navigate])

  // Mark registration complete (called after organization registration)
  const completeRegistration = useCallback((status = true) => {
    setRegistrationComplete(status)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        loading: loading || initialLoad,
        error,
        login,
        logout,
        verifyAuth,
        registrationComplete,
        completeRegistration,
        currentUserPosition,
        reportingToPosition,
        directReportPositions,
        reportingStructureLoaded,
        extractAttributes
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)